import { useCallback, useState } from 'react';
import { isEmptyObject } from '../utils/generic';
import { IProductPrice, Product, ProductPrice } from '@/types';
import {
  buildCompareCompetitorRows,
  type CompareCompetitorRow,
} from '@/lib/utils/prices-comparison-check/compare-competitors';

type ProductWithPrices = {
  id?: string | number;
  cartId?: unknown;
  prices?: IProductPrice[] | ProductPrice[];
  product_prices?: IProductPrice[] | ProductPrice[];
  sale_price?: number;
  price?: number;
};

function getProductPricesArray(product: ProductWithPrices): IProductPrice[] {
  return (product.prices ?? product.product_prices ?? []) as IProductPrice[];
}

export function useCompareFunctions() {
  const [hasCompareList, setHasCompareList] = useState(false);

  const loadCompareComproveInfo = useCallback((product: ProductWithPrices) => {
    const prices = getProductPricesArray(product);
    const { rows, economiaMedia } = buildCompareCompetitorRows(prices, {
      sale_price: product.sale_price,
      price: product.price,
    });

    const hasList = rows.length > 0;
    setHasCompareList(hasList);

    const payload: (CompareCompetitorRow & { active: true })[] = rows.map(
      (r) => ({ ...r, active: true as const }),
    );

    return {
      economiaMedia,
      hasCompareList: hasList,
      payload,
      id: product.id,
      cartId: product.cartId,
    };
  }, []);

  const loadCompareInfo = useCallback(
    (products: Product[]) => {
      return products
        .map((product) => {
          if (isEmptyObject(product)) return null;
          return loadCompareComproveInfo(product as ProductWithPrices);
        })
        .filter(Boolean);
    },
    [loadCompareComproveInfo],
  );

  const getTotalConcorrencia = useCallback(
    (compareInformations: { economiaMedia: number }[], amount: number, discount: number) => {
      return compareInformations.reduce(
        (total, product) => total + product.economiaMedia,
        amount + discount,
      );
    },
    [],
  );

  return {
    loadCompareInfo,
    getTotalConcorrencia,
    hasCompareList,
  };
}
